import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal, Header, Button, Input, Dropdown, List, Label, Icon, Grid } from 'semantic-ui-react';

const GroupOptionsModal = ({
	open,
	setOpen,
	existingOptions,
	setSurveyOptions,
	optionItem,
	surveyStrucRow,
	setSurveyStrucRow,
	setUpdateJsonFile,
}) => {
	const [groupInput, setGroupInput] = useState('');
	const [groups, setGroups] = useState([]);
	const [selectionById, setSelectionById] = useState({});
	const [groupTitles, setGroupTitles] = useState({});

	const uniqueGroupsFromOptions = useMemo(() => {
		const set = new Set();
		(existingOptions || []).forEach((o) => {
			if (o && typeof o.group === 'string' && o.group.trim().length > 0) set.add(o.group.trim());
		});
		return Array.from(set).sort((a, b) => a.localeCompare(b));
	}, [existingOptions]);

	useEffect(() => {
		setGroups(uniqueGroupsFromOptions);
		const nextSelection = {};
		(existingOptions || []).forEach((o) => {
			nextSelection[o.id] = (o.group && typeof o.group === 'string') ? o.group : '';
		});
		setSelectionById(nextSelection);
		// initialize translations from category.group_titles if present
		const existingTitles = (optionItem && optionItem.group_titles && typeof optionItem.group_titles === 'object') ? optionItem.group_titles : {};
		const titlesClone = { ...existingTitles };
		uniqueGroupsFromOptions.forEach((g) => {
			if (!titlesClone[g]) {
				titlesClone[g] = { EN: g, FR: '', POR: '', ES: '' };
			} else {
				titlesClone[g] = {
					EN: (existingTitles[g].EN !== undefined ? existingTitles[g].EN : (existingTitles[g].en || g)) || g,
					FR: existingTitles[g].FR !== undefined ? existingTitles[g].FR : (existingTitles[g].fr || ''),
					POR: existingTitles[g].POR !== undefined ? existingTitles[g].POR : (existingTitles[g].por || ''),
					ES: existingTitles[g].ES !== undefined ? existingTitles[g].ES : (existingTitles[g].es || ''),
				};
			}
		});
		setGroupTitles(titlesClone);
	}, [open, existingOptions, uniqueGroupsFromOptions, optionItem]);

	function onClose() {
		setOpen(false);
	}

	function addGroup() {
		const value = (groupInput || '').trim();
		if (!value) return;
		if (!groups.includes(value)) setGroups([...groups, value].sort((a, b) => a.localeCompare(b)));
		setGroupInput('');

		setGroupTitles((prev) => {
			if (prev[value]) return prev;
			return { ...prev, [value]: { EN: value, FR: '', POR: '', ES: '' } };
		});
	}

	function removeGroup(name) {
		const filtered = groups.filter((g) => g !== name);
		setGroups(filtered);
		// Unassign this group from any selected options
		const clone = { ...selectionById };
		Object.keys(clone).forEach((id) => {
			if (clone[id] === name) clone[id] = '';
		});
		setSelectionById(clone);
		setGroupTitles((prev) => {
			const c = { ...prev };
			delete c[name];
			return c;
		});
	}

	function setSelection(optionId, value) {
		setSelectionById({ ...selectionById, [optionId]: value || '' });
	}

	function onSave() {
		const updatedList = (existingOptions || []).map((o) => ({
			...o,
			group: selectionById[o.id] ? selectionById[o.id] : undefined,
		}));
		setSurveyOptions(updatedList);

		// Build normalized group_titles with uppercase language keys
		// Include all groups that have translations, not just the ones in the groups array
		const normalizedTitles = {};
		const allGroupsWithTranslations = new Set([
			...groups,
			...Object.keys(groupTitles)
		]);
		
		allGroupsWithTranslations.forEach((g) => {
			const t = groupTitles[g] || { EN: g, FR: '', POR: '', ES: '' };
			normalizedTitles[g] = {
				EN: t.EN || g,
				FR: t.FR || '',
				POR: t.POR || '',
				ES: t.ES || '',
			};
		});

		const ExcludedList = (surveyStrucRow || []).filter((sc) => sc.id !== optionItem.id);
		const UpdatedItem = { ...optionItem, options: updatedList, group_titles: normalizedTitles };
		ExcludedList.push(UpdatedItem);
		setSurveyStrucRow(ExcludedList);
		setUpdateJsonFile(Math.random());
		setOpen(false);
	}

	const dropdownOptions = useMemo(() => {
		return [
			{ key: 'none', text: 'None', value: '' },
			...groups.map((g) => ({ key: g, text: g, value: g })),
		];
	}, [groups]);

	return (
		<Modal open={open} onClose={onClose} size="large">
			<Modal.Header>
				<Header as="h2">Manage Group Titles</Header>
			</Modal.Header>
			<Modal.Content scrolling>
				<Section>
					<SectionTitle>Group titles</SectionTitle>
					<GroupAddRow>
						<Input
							placeholder="Add new group title"
							value={groupInput}
							onChange={(e) => setGroupInput(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter') addGroup(); }}
						/>
						<Button color="violet" onClick={addGroup}>Add</Button>
					</GroupAddRow>
					<GroupChips>
						{groups.length === 0 && (
							<NoneHint>No groups yet. Add one above.</NoneHint>
						)}
						{groups.map((g) => (
							<Label key={g} color="violet" style={{ marginBottom: 6 }}>
								{g}
								<Icon name="close" style={{ marginLeft: 8, cursor: 'pointer' }} onClick={() => removeGroup(g)} />
							</Label>
						))}
					</GroupChips>
				</Section>

				<Section>
					<SectionTitle>Translations (EN, FR, POR, ES)</SectionTitle>
					{groups.length === 0 && (
						<NoneHint>Add a group to enable translations.</NoneHint>
					)}
					{groups.map((g) => (
						<GroupTranslation key={g}>
							<Label size="large" basic>{g}</Label>
							<Grid stackable columns={4} style={{ width: '100%' }}>
								<Grid.Column>
									<Input
										label="EN"
										value={(groupTitles[g] && groupTitles[g].EN) || ''}
										onChange={(e) => setGroupTitles({ ...groupTitles, [g]: { ...(groupTitles[g] || {}), EN: e.target.value } })}
									/>
								</Grid.Column>
								<Grid.Column>
									<Input
										label="FR"
										value={(groupTitles[g] && groupTitles[g].FR) || ''}
										onChange={(e) => setGroupTitles({ ...groupTitles, [g]: { ...(groupTitles[g] || {}), FR: e.target.value } })}
									/>
								</Grid.Column>
								<Grid.Column>
									<Input
										label="POR"
										value={(groupTitles[g] && groupTitles[g].POR) || ''}
										onChange={(e) => setGroupTitles({ ...groupTitles, [g]: { ...(groupTitles[g] || {}), POR: e.target.value } })}
									/>
								</Grid.Column>
								<Grid.Column>
									<Input
										label="ES"
										value={(groupTitles[g] && groupTitles[g].ES) || ''}
										onChange={(e) => setGroupTitles({ ...groupTitles, [g]: { ...(groupTitles[g] || {}), ES: e.target.value } })}
									/>
								</Grid.Column>
							</Grid>
						</GroupTranslation>
					))}
				</Section>

				<Section>
					<SectionTitle>Assign options to a group</SectionTitle>
					<List divided relaxed>
						{(existingOptions || []).map((opt) => (
							<List.Item key={opt.id}>
								<List.Content floated="right" style={{ minWidth: 220 }}>
									<Dropdown
										selection
										placeholder="Select group"
										options={dropdownOptions}
										value={selectionById[opt.id] || ''}
										onChange={(e, { value }) => setSelection(opt.id, value)}
									/>
								</List.Content>
								<List.Content>
									<List.Header>{opt.name}</List.Header>
									<List.Description style={{ color: '#888' }}>{opt.id}</List.Description>
								</List.Content>
							</List.Item>
						))}
					</List>
				</Section>
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={onClose}>Close</Button>
				<Button color="violet" onClick={onSave}>Save</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default GroupOptionsModal;

const Section = styled.div`
	margin-bottom: 18px;
`;

const SectionTitle = styled.div`
	font-weight: bold;
	margin-bottom: 10px;
`;

const GroupAddRow = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
	margin-bottom: 8px;
`;

const GroupChips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`;

const NoneHint = styled.div`
	color: #888;
`;

const GroupTranslation = styled.div`
	border: 1px solid #eee;
	border-radius: 6px;
	padding: 10px;
	margin-bottom: 10px;
	display: flex;
	gap: 12px;
	align-items: center;
`;


